import { bootToolPage } from '../lib/boot-tool'
import { openStage } from '../lib/stage'
import { stageMarkup } from '../lib/stage-html'
import { t } from '../i18n/dictionaries'

type Pattern = 'noise' | 'stripe' | 'orbit' | 'pulse'

const PATTERNS: Pattern[] = ['noise', 'stripe', 'orbit', 'pulse']

const locale = bootToolPage('anti-burn-in')
document.body.insertAdjacentHTML(
  'beforeend',
  stageMarkup({ controls: ['exit', 'prev', 'next', 'hide'] }),
)
document.querySelectorAll<HTMLElement>('[data-stage-root] [data-i18n]').forEach((node) => {
  const key = node.dataset.i18n
  if (key) node.textContent = t(locale, key)
})

let pattern: Pattern =
  (document.querySelector<HTMLElement>('[data-pattern].is-active')?.dataset.pattern as Pattern) ||
  'noise'

document.querySelectorAll<HTMLButtonElement>('[data-pattern]').forEach((btn) => {
  btn.addEventListener('click', () => {
    document.querySelectorAll('[data-pattern]').forEach((node) => node.classList.remove('is-active'))
    btn.classList.add('is-active')
    pattern = (btn.dataset.pattern as Pattern) || 'noise'
  })
})

function patternLabel(id: Pattern): string {
  const keys: Record<Pattern, string> = {
    noise: 'anti_burn_in_noise',
    stripe: 'anti_burn_in_stripe',
    orbit: 'anti_burn_in_orbit',
    pulse: 'anti_burn_in_pulse',
  }
  return t(locale, keys[id])
}

function formatRemain(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

document.querySelector('[data-action="start"]')?.addEventListener('click', () => {
  const stage = document.querySelector<HTMLElement>('[data-stage-root]')
  const canvasHost = document.querySelector<HTMLElement>('[data-stage-canvas]')
  const speedInput = document.querySelector<HTMLInputElement>('#abi-speed')
  const durationSelect = document.querySelector<HTMLSelectElement>('#abi-duration')
  if (!stage || !canvasHost) return

  canvasHost.replaceChildren()
  canvasHost.style.cssText = 'background:#000;overflow:hidden'

  const canvas = document.createElement('canvas')
  canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block'
  canvasHost.appendChild(canvas)
  const ctx = canvas.getContext('2d', { alpha: false })
  if (!ctx) {
    return
  }

  const offscreen = document.createElement('canvas')
  const offCtx = offscreen.getContext('2d', { alpha: false })
  if (!offCtx) {
    return
  }

  let mode = pattern
  let raf = 0
  let running = true
  const startedAt = performance.now()
  let noiseBuf: ImageData | null = null
  let noiseW = 0
  let noiseH = 0
  let frame = 0

  const durationMin = Number(durationSelect?.value || 0)
  const endsAt = durationMin > 0 ? startedAt + durationMin * 60_000 : 0

  const resize = () => {
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    const w = Math.max(1, Math.floor(window.innerWidth * dpr))
    const h = Math.max(1, Math.floor(window.innerHeight * dpr))
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w
      canvas.height = h
      noiseBuf = null
    }
  }

  const ensureNoise = () => {
    noiseW = Math.max(96, Math.floor(canvas.width / 8))
    noiseH = Math.max(54, Math.floor(canvas.height / 8))
    offscreen.width = noiseW
    offscreen.height = noiseH
    noiseBuf = offCtx.createImageData(noiseW, noiseH)
  }

  const paintNoise = (intensity: number) => {
    if (!noiseBuf) ensureNoise()
    if (!noiseBuf) return
    // Skip frames at low speed to save power.
    const skip = intensity <= 2 ? 3 : intensity <= 5 ? 2 : 1
    frame += 1
    if (frame % skip !== 0) {
      ctx.imageSmoothingEnabled = false
      ctx.drawImage(offscreen, 0, 0, canvas.width, canvas.height)
      return
    }
    const data = noiseBuf.data
    for (let i = 0; i < data.length; i += 4) {
      data[i] = (Math.random() * 255) | 0
      data[i + 1] = (Math.random() * 255) | 0
      data[i + 2] = (Math.random() * 255) | 0
      data[i + 3] = 255
    }
    offCtx.putImageData(noiseBuf, 0, 0)
    ctx.imageSmoothingEnabled = false
    ctx.drawImage(offscreen, 0, 0, canvas.width, canvas.height)
  }

  const paintStripe = (tSec: number, speed: number) => {
    const w = canvas.width
    const h = canvas.height
    const band = Math.max(24, w / 10)
    const offset = ((tSec * speed * 80) % (band * 6)) - band
    const colors = ['#121212', '#c62828', '#2e7d32', '#1565c0', '#f9a825', '#f5f5f5']
    for (let i = -1; i < w / band + 2; i++) {
      ctx.fillStyle = colors[((i % colors.length) + colors.length) % colors.length]
      ctx.fillRect(i * band + offset, 0, band + 1, h)
    }
  }

  const paintOrbit = (tSec: number, speed: number) => {
    const w = canvas.width
    const h = canvas.height
    ctx.fillStyle = '#050505'
    ctx.fillRect(0, 0, w, h)
    const blobs = [
      { hue: 12, r: 0.28, ox: 0.22, oy: 0.3, px: 0.7, py: 0.55 },
      { hue: 210, r: 0.32, ox: 0.7, oy: 0.65, px: 0.55, py: 0.4 },
      { hue: 140, r: 0.24, ox: 0.45, oy: 0.2, px: 0.35, py: 0.75 },
    ]
    for (const b of blobs) {
      const x = (b.ox + Math.sin(tSec * 0.35 * speed + b.px) * 0.28) * w
      const y = (b.oy + Math.cos(tSec * 0.28 * speed + b.py) * 0.26) * h
      const radius = b.r * Math.min(w, h)
      const g = ctx.createRadialGradient(x, y, 0, x, y, radius)
      g.addColorStop(0, `hsla(${b.hue}, 70%, 55%, 0.85)`)
      g.addColorStop(0.55, `hsla(${b.hue + 20}, 60%, 40%, 0.35)`)
      g.addColorStop(1, 'hsla(0,0%,0%,0)')
      ctx.fillStyle = g
      ctx.fillRect(0, 0, w, h)
    }
  }

  const paintPulse = (tSec: number, speed: number) => {
    const hue = (tSec * 18 * speed) % 360
    const light = 38 + Math.sin(tSec * 0.7 * speed) * 12
    ctx.fillStyle = `hsl(${hue} 55% ${light}%)`
    ctx.fillRect(0, 0, canvas.width, canvas.height)
  }

  const syncLabel = (now: number) => {
    const base = patternLabel(mode)
    if (!endsAt) return base
    return `${base} · ${formatRemain(endsAt - now)}`
  }

  let labelEl = stage.querySelector<HTMLElement>('[data-stage-label]')
  let cleanup = () => {}

  const exitAll = () => {
    if (!running && canvasHost.childElementCount === 0) return
    running = false
    cancelAnimationFrame(raf)
    canvasHost.replaceChildren()
    canvasHost.removeAttribute('style')
    cleanup()
  }

  const step = (now: number) => {
    if (!running) return
    if (endsAt && now >= endsAt) {
      exitAll()
      return
    }
    resize()
    const speed = Number(speedInput?.value || 4)
    const tSec = (now - startedAt) / 1000
    if (mode === 'noise') paintNoise(speed)
    else if (mode === 'stripe') paintStripe(tSec, speed)
    else if (mode === 'orbit') paintOrbit(tSec, speed)
    else paintPulse(tSec, speed)

    if (labelEl) labelEl.textContent = syncLabel(now)
    raf = requestAnimationFrame(step)
  }

  const shift = (dir: -1 | 1) => {
    const idx = PATTERNS.indexOf(mode)
    mode = PATTERNS[(idx + dir + PATTERNS.length) % PATTERNS.length]
    document.querySelectorAll('[data-pattern]').forEach((node) => {
      node.classList.toggle('is-active', (node as HTMLElement).dataset.pattern === mode)
    })
    pattern = mode
  }

  cleanup = openStage(stage, {
    label: syncLabel(performance.now()),
    onPrev: () => shift(-1),
    onNext: () => shift(1),
    onExit: exitAll,
  })

  labelEl = stage.querySelector<HTMLElement>('[data-stage-label]')
  raf = requestAnimationFrame(step)
})
